import { useMemo, useState } from "react";
import { ArrowRight, GitCompare, History, Minus, Plus, RefreshCw } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Badge, Button, EmptyState, NativeSelect, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, Timeline, cn, type TimelineItem } from "@ncp/ui";
import { parsePatch, type JsonPatchOp } from "../releases/api";
import { useDefinitionDiff, useDefinitionVersions, type DefinitionResource } from "../workspaces/api";
import type { DefinitionVersion } from "./api";

interface Props {
  formKey: string;
  /** Which versioned definition API to read (`/api/platform/{resource}`); forms by default. */
  resource?: DefinitionResource;
  currentVersion?: number;
  onOpenVersion?: (version: number) => void;
}

const STATUS_VARIANT: Record<string, "success" | "warning" | "info" | "default" | "danger"> = { Published: "success", InReview: "warning", Draft: "info", Retired: "default", Rejected: "danger" };

/** Version history of a definition (timeline) and a picker to compare any two versions as an RFC 6902 patch with a plain-language summary. */
export function VersionsPanel({ formKey, resource = "forms", currentVersion, onOpenVersion }: Props) {
  const { t, i18n } = useTranslation();
  const versions = useDefinitionVersions(resource, formKey);
  const list = versions.data ?? [];
  const sorted = useMemo(() => [...list].sort((a, b) => b.version - a.version), [list]);
  const defaults = useMemo(() => {
    if (sorted.length < 2) return { from: sorted[0]?.version, to: sorted[0]?.version };
    const published = sorted.find((v) => v.status === "Published");
    const latest = sorted[0]!;
    if (published && published.version !== latest.version) return { from: published.version, to: latest.version };
    return { from: sorted[1]!.version, to: latest.version };
  }, [sorted]);
  const [from, setFrom] = useState<number | undefined>();
  const [to, setTo] = useState<number | undefined>();
  const fromV = from ?? defaults.from;
  const toV = to ?? defaults.to;
  const diff = useDefinitionDiff(resource, formKey, fromV, toV);
  const fmt = (iso?: string | null) => (iso ? new Intl.DateTimeFormat(i18n.language === "ar" ? "ar-AE" : "en-GB", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso)) : "—");

  if (versions.isLoading) return <div className="space-y-2"><Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" /></div>;
  if (sorted.length === 0) return <EmptyState icon={History} title={t("admin:forms.versions.emptyTitle")} description={t("admin:forms.versions.emptyBody")} />;

  const items: TimelineItem[] = sorted.map((v) => ({
    id: v.id,
    title: <span className="flex flex-wrap items-center gap-1.5"><span className="font-medium">v{v.version}</span><Badge variant={STATUS_VARIANT[v.status] ?? "default"} size="sm">{v.status}</Badge>{v.version === currentVersion ? <Badge variant="outline" size="sm">{t("admin:forms.versions.editing")}</Badge> : null}</span>,
    detail: <span className="space-y-0.5">{v.changeNote ? <span className="block text-foreground">{v.changeNote}</span> : null}<span className="block text-[11px]">{v.publishedAt ? t("admin:forms.versions.publishedAt", { at: fmt(v.publishedAt) }) : t("admin:forms.versions.updatedAt", { at: fmt(v.updatedAt) })}{v.publishedByChangeSetId ? ` · ${t("admin:forms.versions.changeSet")} ${v.publishedByChangeSetId.slice(0, 8)}` : ""}</span>{onOpenVersion && v.version !== currentVersion ? <Button size="sm" variant="ghost" className="mt-1 h-6 px-2 text-[11px]" onClick={() => onOpenVersion(v.version)}>{t("admin:common.open")} v{v.version}</Button> : null}</span>,
    time: fmt(v.publishedAt ?? v.updatedAt),
    actor: v.publishedByName ?? v.createdByName ?? undefined,
    kind: v.status === "Published" ? "milestone" : v.status === "Retired" ? "system" : "human",
  }));

  return (
    <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
      <div className="rounded-xl border border-sand-200 bg-white/80 p-4">
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold"><History className="size-4" /> {t("admin:forms.versions.title")}</h3>
        <Timeline items={items} />
      </div>
      <div className="rounded-xl border border-sand-200 bg-white/80 p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold"><GitCompare className="size-4" /> {t("admin:forms.versions.compare")}</h3>
          <div className="ms-auto flex items-center gap-1.5">
            <VersionSelect versions={sorted} value={fromV} onChange={setFrom} />
            <ArrowRight className="size-4 text-muted-foreground rtl:rotate-180" />
            <VersionSelect versions={sorted} value={toV} onChange={setTo} />
          </div>
        </div>
        {sorted.length < 2 ? <p className="rounded-xl bg-sand-50 p-4 text-sm text-muted-foreground">{t("admin:forms.versions.onlyOne")}</p> : diff.isLoading ? <Skeleton className="h-40" /> : diff.data ? <PatchView patchJson={diff.data.patchJson} summary={diff.data.summary} before={diff.data.beforeJson} after={diff.data.afterJson} /> : null}
      </div>
    </div>
  );
}

function VersionSelect({ versions, value, onChange }: { versions: DefinitionVersion[]; value?: number; onChange: (v: number) => void }) {
  return (
    <NativeSelect value={value ?? ""} className="h-8 w-40 text-xs" onChange={(e) => onChange(Number(e.target.value))}>
      {versions.map((v) => <option key={v.id} value={v.version}>v{v.version} · {v.status}</option>)}
    </NativeSelect>
  );
}

function OpBadge({ op }: { op: JsonPatchOp["op"] }) {
  const cls = op === "add" ? "bg-success-50 text-success-700" : op === "remove" ? "bg-danger-50 text-danger-700" : "bg-warning-50 text-warning-700";
  const Icon = op === "add" ? Plus : op === "remove" ? Minus : RefreshCw;
  return <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px] uppercase", cls)}><Icon className="size-3" /> {op}</span>;
}

function short(value: unknown, max = 240): string {
  const s = typeof value === "string" ? value : JSON.stringify(value);
  if (s === undefined) return "";
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function pretty(json?: string | null): string {
  if (!json) return "";
  try { return JSON.stringify(JSON.parse(json), null, 2); } catch { return json; }
}

export function PatchView({ patchJson, summary, before, after }: { patchJson: string; summary: string[]; before?: string | null; after?: string | null }) {
  const { t } = useTranslation();
  const ops = useMemo(() => parsePatch(patchJson), [patchJson]);
  const grouped = useMemo(() => {
    const g = new Map<string, JsonPatchOp[]>();
    for (const op of ops) { const root = op.path.split("/")[1] ?? ""; const list = g.get(root) ?? []; list.push(op); g.set(root, list); }
    return [...g.entries()];
  }, [ops]);
  return (
    <Tabs defaultValue="summary">
      <TabsList className="mb-3">
        <TabsTrigger value="summary">{t("admin:forms.versions.summary")}</TabsTrigger>
        <TabsTrigger value="patch">{t("admin:releases.diff.patch")} <Badge variant="outline" size="sm" className="ms-1">{ops.length}</Badge></TabsTrigger>
        <TabsTrigger value="before">{t("admin:releases.diff.before")}</TabsTrigger>
        <TabsTrigger value="after">{t("admin:releases.diff.after")}</TabsTrigger>
      </TabsList>
      <TabsContent value="summary">
        {summary.length === 0 ? <p className="rounded-xl bg-sand-50 p-4 text-sm text-muted-foreground">{t("admin:releases.diff.noChanges")}</p> : <ul className="space-y-1 text-sm">{summary.map((s, i) => <li key={i} className="flex gap-2 rounded-lg bg-sand-50 px-3 py-1.5"><span className="text-aegold-700">•</span><span>{s}</span></li>)}</ul>}
      </TabsContent>
      <TabsContent value="patch">
        {ops.length === 0 ? <p className="rounded-xl bg-sand-50 p-4 text-sm text-muted-foreground">{t("admin:releases.diff.noChanges")}</p> : (
          <div className="space-y-3">
            {grouped.map(([root, list]) => (
              <div key={root} className="rounded-xl border border-sand-200">
                <div className="flex items-center justify-between border-b border-sand-100 bg-sand-50 px-3 py-1.5"><span className="font-mono text-xs font-semibold">/{root}</span><Badge variant="outline" size="sm">{list.length}</Badge></div>
                <ul className="divide-y divide-sand-100 text-xs" dir="ltr">
                  {list.map((op, i) => (
                    <li key={i} className="grid grid-cols-[auto_minmax(0,1fr)] gap-3 px-3 py-1.5">
                      <OpBadge op={op.op} />
                      <div className="min-w-0"><p className="truncate font-mono text-[11px] text-sand-700">{op.path}</p>{op.op !== "remove" ? <p className={cn("mt-0.5 break-all font-mono text-[11px]", op.op === "add" ? "text-success-700" : "text-warning-800")}>{short(op.value)}</p> : null}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </TabsContent>
      <TabsContent value="before"><pre className="max-h-[480px] overflow-auto rounded-xl bg-sand-50 p-3 font-mono text-[11px] leading-relaxed" dir="ltr">{pretty(before) || "—"}</pre></TabsContent>
      <TabsContent value="after"><pre className="max-h-[480px] overflow-auto rounded-xl bg-sand-50 p-3 font-mono text-[11px] leading-relaxed" dir="ltr">{pretty(after) || "—"}</pre></TabsContent>
    </Tabs>
  );
}
